
<template>
  <section class="calculator-module">
    <div class="module-header">
      <h2>{{ t('nutrients.title').value }}</h2>
      <span class="toggle-icon">▼</span>
    </div>
    <div class="module-content">
      <div class="calculator-intro">
        <h3>{{ t('nutrients.subtitle').value }}</h3>
        <p>{{ t('nutrients.description').value }}</p>
      </div>
      
      <div class="calculator-tabs">
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'dilution' }"
          @click="activeTab = 'dilution'"
        >
          {{ t('nutrients.tabs.dilution').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'carbon' }"
          @click="activeTab = 'carbon'"
        >
          {{ t('nutrients.tabs.carbon').value }}
        </button>
        <button
          class="tab-btn"
          :class="{ active: activeTab === 'advice' }"
          @click="activeTab = 'advice'"
        >
          {{ t('nutrients.tabs.advice').value }}
        </button>
      </div>
      
      <!-- 营养盐稀释计算 -->
      <div v-if="activeTab === 'dilution'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('nutrients.sections.basicInfo').value }}</h4>
          <div class="form-group">
            <label for="nutrients_volume">{{ t('nutrients.labels.waterVolume').value }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="nutrients_volume"
                v-model="dilutionData.volume"
                class="form-control"
                :placeholder="t('nutrients.placeholders.waterVolume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolume"
                type="button"
              >
                {{ t('nutrients.buttons.useSavedVolume').value }}
              </button>
            </div>
            <small class="form-hint" v-if="storedWaterVolume">
              {{ t('nutrients.labels.savedVolume').value }} {{ storedWaterVolume }} L
            </small>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('nutrients.sections.nutrientParams').value }}</h4>
          <div class="form-group">
            <label for="nutrient_type">{{ t('nutrients.labels.nutrientType').value }}</label>
            <select
              id="nutrient_type"
              v-model="dilutionData.nutrient_type"
              class="select-control"
            >
              <option value="no3">{{ t('nutrients.options.nutrientType.no3').value }}</option>
              <option value="po4">{{ t('nutrients.options.nutrientType.po4').value }}</option>
            </select>
          </div>
          
          <div class="form-row">
            <div class="form-group">
              <label for="current_concentration">{{ t('nutrients.labels.currentConcentration').value }}</label>
              <input
                type="number"
                id="current_concentration"
                v-model="dilutionData.current_concentration"
                class="form-control"
                :placeholder="dilutionData.nutrient_type === 'no3' ? t('nutrients.placeholders.currentConcentrationNO3').value : t('nutrients.placeholders.currentConcentrationPO4').value"
                :step="dilutionData.nutrient_type === 'no3' ? '1' : '0.001'"
                min="0"
              >
            </div>
            <div class="form-group">
              <label for="target_concentration">{{ t('nutrients.labels.targetConcentration').value }}</label>
              <input
                type="number"
                id="target_concentration"
                v-model="dilutionData.target_concentration"
                class="form-control"
                :placeholder="dilutionData.nutrient_type === 'no3' ? t('nutrients.placeholders.targetConcentrationNO3').value : t('nutrients.placeholders.targetConcentrationPO4').value"
                :step="dilutionData.nutrient_type === 'no3' ? '1' : '0.001'"
                min="0"
              >
            </div>
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateDilution"
          :disabled="!canCalculateDilution"
        >
          {{ t('nutrients.buttons.calculateDilution').value }}
        </button>
      </div>
      
      <!-- 碳源投加计算 -->
      <div v-if="activeTab === 'carbon'" class="calculator-form">
        <div class="form-section">
          <h4>{{ t('nutrients.sections.basicInfo').value }}</h4>
          <div class="form-group">
            <label for="carbon_volume">{{ t('nutrients.labels.waterVolume').value }}</label>
            <div class="input-with-action">
              <input
                type="number"
                id="carbon_volume"
                v-model="carbonData.volume"
                class="form-control"
                :placeholder="t('nutrients.placeholders.waterVolume').value"
                step="0.1"
                min="1"
              >
              <button
                v-if="storedWaterVolume"
                class="use-stored-btn"
                @click="useStoredVolumeCarbon"
                type="button"
              >
                {{ t('nutrients.buttons.useSavedVolume').value }}
              </button>
            </div>
            <small class="form-hint" v-if="storedWaterVolume">
              {{ t('nutrients.labels.savedVolume').value }} {{ storedWaterVolume }} L
            </small>
          </div>
        </div>
        
        <div class="form-section">
          <h4>{{ t('nutrients.sections.carbonParams').value }}</h4>
          <div class="form-group">
            <label for="carbon_type">{{ t('nutrients.labels.carbonType').value }}</label>
            <select
              id="carbon_type"
              v-model="carbonData.carbon_type"
              class="select-control"
            >
              <option value="vodka">{{ t('nutrients.options.carbonType.vodka').value }}</option>
              <option value="vinegar">{{ t('nutrients.options.carbonType.vinegar').value }}</option>
              <option value="bio-pellets">{{ t('nutrients.options.carbonType.bioPellets').value }}</option>
              <option value="nopox">{{ t('nutrients.options.carbonType.nopox').value }}</option>
              <option value="custom">{{ t('nutrients.options.carbonType.custom').value }}</option>
            </select>
          </div>
          
          <div class="form-group" v-if="carbonData.carbon_type !== 'bio-pellets'">
            <label for="current_no3">{{ t('nutrients.labels.currentNO3').value }}</label>
            <input
              type="number"
              id="current_no3"
              v-model="carbonData.current_no3"
              class="form-control"
              :placeholder="t('nutrients.placeholders.currentNO3').value"
              step="0.1"
              min="0"
            >
          </div>
          
          <div class="form-group" v-if="carbonData.carbon_type === 'custom'">
            <label for="custom_dosage">{{ t('nutrients.labels.customDosage').value }}</label>
            <input
              type="number"
              id="custom_dosage"
              v-model="carbonData.custom_dosage"
              class="form-control"
              :placeholder="t('nutrients.placeholders.customDosage').value"
              step="0.1"
              min="0"
            >
          </div>
        </div>
        
        <button
          class="calculate-btn"
          @click="calculateCarbonDosage"
          :disabled="!canCalculateCarbon"
        >
          {{ t('nutrients.buttons.calculateCarbon').value }}
        </button>
      </div>
      
      <!-- 管理建议 -->
      <div v-if="activeTab === 'advice'" class="advice-section">
        <div class="advice-card">
          <h4>{{ t('nutrients.advice.title').value }}</h4>
          
          <div class="advice-category">
            <h5>{{ t('nutrients.advice.no3Levels.title').value }}</h5>
            <div class="advice-levels">
              <div class="level-item good">
                <span class="level-label">{{ t('nutrients.advice.no3Levels.ideal.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.no3Levels.ideal.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.no3Levels.ideal.desc').value }}</span>
              </div>
              <div class="level-item moderate">
                <span class="level-label">{{ t('nutrients.advice.no3Levels.acceptable.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.no3Levels.acceptable.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.no3Levels.acceptable.desc').value }}</span>
              </div>
              <div class="level-item high">
                <span class="level-label">{{ t('nutrients.advice.no3Levels.high.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.no3Levels.high.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.no3Levels.high.desc').value }}</span>
              </div>
              <div class="level-item critical">
                <span class="level-label">{{ t('nutrients.advice.no3Levels.critical.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.no3Levels.critical.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.no3Levels.critical.desc').value }}</span>
              </div>
            </div>
          </div>
          
          <div class="advice-category">
            <h5>{{ t('nutrients.advice.po4Levels.title').value }}</h5>
            <div class="advice-levels">
              <div class="level-item good">
                <span class="level-label">{{ t('nutrients.advice.po4Levels.ideal.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.po4Levels.ideal.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.po4Levels.ideal.desc').value }}</span>
              </div>
              <div class="level-item moderate">
                <span class="level-label">{{ t('nutrients.advice.po4Levels.acceptable.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.po4Levels.acceptable.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.po4Levels.acceptable.desc').value }}</span>
              </div>
              <div class="level-item high">
                <span class="level-label">{{ t('nutrients.advice.po4Levels.high.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.po4Levels.high.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.po4Levels.high.desc').value }}</span>
              </div>
              <div class="level-item critical">
                <span class="level-label">{{ t('nutrients.advice.po4Levels.critical.label').value }}</span>
                <span class="level-value">{{ t('nutrients.advice.po4Levels.critical.value').value }}</span>
                <span class="level-desc">{{ t('nutrients.advice.po4Levels.critical.desc').value }}</span>
              </div>
            </div>
          </div>
          
          <div class="advice-category">
            <h5>{{ t('nutrients.advice.strategies.title').value }}</h5>
            <div class="strategy-list">
              <div class="strategy-item">
                <h6>{{ t('nutrients.advice.strategies.waterChange.title').value }}</h6>
                <p>{{ t('nutrients.advice.strategies.waterChange.desc').value }}</p>
                <ul>
                  <li v-for="tip in t('nutrients.advice.strategies.waterChange.tips').value" :key="tip">{{ tip }}</li>
                </ul>
              </div>
              <div class="strategy-item">
                <h6>{{ t('nutrients.advice.strategies.carbonDosing.title').value }}</h6>
                <p>{{ t('nutrients.advice.strategies.carbonDosing.desc').value }}</p>
                <ul>
                  <li v-for="tip in t('nutrients.advice.strategies.carbonDosing.tips').value" :key="tip">{{ tip }}</li>
                </ul>
              </div>
              <div class="strategy-item">
                <h6>{{ t('nutrients.advice.strategies.physicalFiltration.title').value }}</h6>
                <p>{{ t('nutrients.advice.strategies.physicalFiltration.desc').value }}</p>
                <ul>
                  <li v-for="tip in t('nutrients.advice.strategies.physicalFiltration.tips').value" :key="tip">{{ tip }}</li>
                </ul>
              </div>
              <div class="strategy-item">
                <h6>{{ t('nutrients.advice.strategies.biologicalControl.title').value }}</h6>
                <p>{{ t('nutrients.advice.strategies.biologicalControl.desc').value }}</p>
                <ul>
                  <li v-for="tip in t('nutrients.advice.strategies.biologicalControl.tips').value" :key="tip">{{ tip }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 稀释计算结果 -->
      <div v-if="dilutionResults.show" class="results-section">
        <div class="results-card">
          <h4>{{ t('nutrients.results.dilution.title').value }}</h4>
          
          <div class="result-grid">
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.dilution.waterToChange').value }}:</span>
              <span class="result-value">{{ dilutionResults.waterToChange }} L</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.dilution.percentage').value }}:</span>
              <span class="result-value">{{ dilutionResults.percentage }}%</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.dilution.nutrientType').value }}:</span>
              <span class="result-value">{{ dilutionResults.nutrientType }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.dilution.concentrationReduction').value }}:</span>
              <span class="result-value">{{ dilutionResults.concentrationReduction }}</span>
            </div>
          </div>
          
          <div class="frequency-advice">
            <h5>💡 {{ t('nutrients.results.dilution.note').value }}</h5>
            <p>{{ dilutionResults.note }}</p>
            <p v-if="dilutionResults.percentage > 30" class="warning-text">
              {{ t('nutrients.results.dilution.warning').value }}
            </p>
          </div>
        </div>
      </div>
      
      <!-- 碳源计算结果 -->
      <div v-if="carbonResults.show" class="results-section">
        <div class="results-card">
          <h4>{{ t('nutrients.results.carbon.title').value }}</h4>
          
          <div class="result-grid">
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.carbon.dailyDosage').value }}:</span>
              <span class="result-value">{{ carbonResults.dailyDosage }} mL</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.carbon.carbonType').value }}:</span>
              <span class="result-value">{{ carbonResults.carbonType }}</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.carbon.volume').value }}:</span>
              <span class="result-value">{{ carbonResults.volume }} L</span>
            </div>
            <div class="result-item">
              <span class="result-label">{{ t('nutrients.results.carbon.currentNO3').value }}:</span>
              <span class="result-value">{{ carbonResults.currentNO3 }} ppm</span>
            </div>
          </div>
          
          <div class="safety-note">
            <h5>{{ t('nutrients.results.carbon.safetyNote').value }}</h5>
            <p>{{ carbonResults.safetyNote }}</p>
            <ul>
              <li v-for="tip in t('nutrients.results.carbon.safetyTips').value" :key="tip">{{ tip }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { useCalculatorStore } from '../stores/calculator'
import { useI18n } from '../composables/useI18n'

const calculatorStore = useCalculatorStore()
const { t } = useI18n()

// 标签页状态
const activeTab = ref<'dilution' | 'carbon' | 'advice'>('dilution')

// 稀释计算数据
const dilutionData = reactive({
  volume: '100',
  nutrient_type: 'no3',
  current_concentration: '25',
  target_concentration: '5'
})

// 碳源计算数据
const carbonData = reactive({
  volume: '100',
  carbon_type: 'vodka',
  current_no3: '25',
  custom_dosage: '0.5'
})

// 计算结果
const dilutionResults = reactive({
  show: false,
  waterToChange: 0,
  percentage: 0,
  nutrientType: '',
  concentrationReduction: '',
  note: ''
})

const carbonResults = reactive({
  show: false,
  dailyDosage: 0,
  carbonType: '',
  volume: 0,
  currentNO3: 0,
  safetyNote: ''
})

// 计算属性
const storedWaterVolume = computed(() => {
  return calculatorStore.waterVolume
})

const canCalculateDilution = computed(() => {
  return dilutionData.volume && dilutionData.current_concentration && dilutionData.target_concentration
})

const canCalculateCarbon = computed(() => {
  if (carbonData.carbon_type === 'bio-pellets') {
    return carbonData.volume
  }
  return carbonData.volume && carbonData.current_no3
})

// 使用已保存的水体体积
const useStoredVolume = () => {
  if (storedWaterVolume.value) {
    dilutionData.volume = storedWaterVolume.value.toString()
  }
}

const useStoredVolumeCarbon = () => {
  if (storedWaterVolume.value) {
    carbonData.volume = storedWaterVolume.value.toString()
  }
}

// 计算稀释换水量
const calculateDilution = () => {
  const volume = parseFloat(dilutionData.volume)
  const current = parseFloat(dilutionData.current_concentration)
  const target = parseFloat(dilutionData.target_concentration)

  const calculation = calculatorStore.calculateNutrientDilution(
    current,
    target,
    volume
  )

  dilutionResults.waterToChange = calculation.waterToChange
  dilutionResults.percentage = calculation.percentage
  dilutionResults.nutrientType = dilutionData.nutrient_type === 'no3'
    ? t('nutrients.results.dilution.nutrientTypeNO3').value
    : t('nutrients.results.dilution.nutrientTypePO4').value
  dilutionResults.concentrationReduction = `${current} → ${target} ${dilutionData.nutrient_type === 'no3' ? 'ppm' : 'ppm'}`
  dilutionResults.note = calculation.note
  dilutionResults.show = true
}

// 计算碳源剂量
const calculateCarbonDosage = () => {
  const volume = parseFloat(carbonData.volume)
  const currentNO3 = carbonData.current_no3 ? parseFloat(carbonData.current_no3) : 0

  let dosagePer100L = 0
  let carbonTypeName = ''
  let safetyNote = ''

  switch (carbonData.carbon_type) {
    case 'vodka':
      dosagePer100L = 0.1 + (currentNO3 * 0.02) // 基础剂量 + NO₃调整
      carbonTypeName = t('nutrients.results.carbon.carbonTypeNames.vodka').value
      safetyNote = t('nutrients.results.carbon.safetyNotes.vodka').value
      break
    case 'vinegar':
      dosagePer100L = 1 + (currentNO3 * 0.2) // 基础剂量 + NO₃调整
      carbonTypeName = t('nutrients.results.carbon.carbonTypeNames.vinegar').value
      safetyNote = t('nutrients.results.carbon.safetyNotes.vinegar').value
      break
    case 'bio-pellets':
      dosagePer100L = 100 // 生物豆按体积计算，100mL/100L
      carbonTypeName = t('nutrients.results.carbon.carbonTypeNames.bioPellets').value
      safetyNote = t('nutrients.results.carbon.safetyNotes.bioPellets').value
      break
    case 'nopox':
      dosagePer100L = 0.5 + (currentNO3 * 0.05) // 基础剂量 + NO₃调整
      carbonTypeName = t('nutrients.results.carbon.carbonTypeNames.nopox').value
      safetyNote = t('nutrients.results.carbon.safetyNotes.nopox').value
      break
    case 'custom':
      dosagePer100L = parseFloat(carbonData.custom_dosage)
      carbonTypeName = t('nutrients.results.carbon.carbonTypeNames.custom').value
      safetyNote = t('nutrients.results.carbon.safetyNotes.custom').value
      break
  }

  // 计算每日剂量
  const dailyDosage = (dosagePer100L * volume) / 100

  carbonResults.dailyDosage = Math.round(dailyDosage * 100) / 100
  carbonResults.carbonType = carbonTypeName
  carbonResults.volume = volume
  carbonResults.currentNO3 = currentNO3
  carbonResults.safetyNote = safetyNote
  carbonResults.show = true
}

// 组件挂载
onMounted(() => {
  calculatorStore.getWaterVolume()
})
</script>

<style scoped>
/* 样式已经在外部CSS文件中定义，这里不需要重复定义 */

.advice-section {
  margin-top: 20px;
}

.advice-card {
  padding: 20px;
  background: white;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.advice-category {
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid #f1f1f1;
}

.advice-category:last-child {
  border-bottom: none;
  margin-bottom: 0;
}

.advice-category h5 {
  color: #007bff;
  margin-bottom: 15px;
  font-size: 1.1rem;
}

.advice-levels {
  display: grid;
  gap: 10px;
}

.level-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 6px;
  border-left: 4px solid;
}

.level-item.good {
  background: #d4edda;
  border-left-color: #28a745;
}

.level-item.moderate {
  background: #fff3cd;
  border-left-color: #ffc107;
}

.level-item.high {
  background: #ffeaa7;
  border-left-color: #fd7e14;
}

.level-item.critical {
  background: #f8d7da;
  border-left-color: #dc3545;
}

.level-label {
  font-weight: 600;
  color: #495057;
}

.level-value {
  font-weight: 700;
  color: #007bff;
}

.level-desc {
  font-size: 0.875rem;
  color: #6c757d;
  text-align: right;
}

.strategy-list {
  display: grid;
  gap: 15px;
}

.strategy-item {
  padding: 15px;
  background: #f8f9fa;
  border-radius: 6px;
  border-left: 3px solid #007bff;
}

.strategy-item h6 {
  margin: 0 0 8px 0;
  color: #007bff;
  font-size: 1rem;
}

.strategy-item p {
  margin: 0 0 10px 0;
  color: #495057;
}

.strategy-item ul {
  margin: 0;
  padding-left: 20px;
  color: #6c757d;
}

.strategy-item li {
  margin-bottom: 4px;
}

.warning-text {
  color: #dc3545;
  font-weight: 500;
  margin-top: 10px;
  padding: 8px;
  background: #f8d7da;
  border-radius: 4px;
  border-left: 3px solid #dc3545;
}

/* 安全使用指南样式 */
.safety-note {
  margin-top: 15px;
  padding: 15px;
  background: #fff3cd;
  border-radius: 6px;
  border-left: 4px solid #ffc107;
}

.safety-note h5 {
  margin: 0 0 10px 0;
  color: #856404;
  font-size: 1em;
}

.safety-note p {
  margin: 0 0 10px 0;
  color: #856404;
  line-height: 1.4;
}

.safety-note ul {
  margin: 0;
  padding-left: 20px;
  color: #856404;
  list-style-position: inside;
}

.safety-note li {
  margin-bottom: 5px;
  line-height: 1.4;
  text-indent: -20px;
  padding-left: 20px;
}
</style>